// src/types/scan.types.ts - Scan and scan metadata types aligned with backend API

/**
 * Scan status enum matching backend scanStatus.enum.ts
 */
export enum ScanStatus {
  TempSaved = 'tempsaved',      // Temporarily saved on device
  Uploaded = 'uploaded',        // Successfully uploaded to S3
  Processing = 'processing',    // In reconstruction processing
  Completed = 'completed',      // Processing complete
  Failed = 'failed',            // Processing failed
  SavedLocally = 'saved-locally', // Saved locally on device
  Archived = 'archived',        // Archived by user
  Deleted = 'deleted',          // Soft-deleted; excluded from default queries
}

/**
 * Scan visibility enum matching backend scanVisibility.enum.ts
 */
export enum ScanVisibility {
  PRIVATE = 'private',
  SHARED = 'shared',
  PUBLIC = 'public'
}

/**
 * Location information for scans
 */
export interface ScanLocation {
  latitude: number;
  longitude: number;
  name?: string;
}

/**
 * Scan version interface matching backend IScanVersion
 */
export interface ScanVersion {
  versionId: string;
  scanId: string;
  version: number;
  createdByUserId: string;
  status: ScanStatus;
  
  // Additional status tracking
  subStatus?: string;
  
  // Storage paths
  tempFolderPath?: string;    // Temporary storage (from mobile app)
  s3FolderPath?: string;      // Cloud storage for uploaded images
  sequenceUuid?: string;      // Sequence UUID for reconstruction tracking
  gaussModelPath?: string;    // Gauss reconstruction path
  pipelineJsonPath?: string;  // EXIF and camera data for reconstruction
  
  // Technical details
  inputImageCount?: number;
  reconstructionFolder: string;
  thumbnailUrl: string;
  flythroughUrl: string;
  
  // Processing details
  processingStartedAt?: Date;
  processingCompletedAt?: Date;
  processingLogs?: string[];
  errorDetails?: any;
  
  // Version metadata
  changelog?: string;
  quality?: string;
  fileSize?: number;
  isFromMobileApp?: boolean;  // Flag to differentiate source
  
  // Standard timestamps
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Main scan interface matching backend IScan interface
 */
export interface Scan {
  scanId: string;
  creatorId: string;
  title: string;
  description?: string;
  visibility: ScanVisibility;
  
  // Metadata and organization
  tags?: string[];
  category?: string;
  location?: ScanLocation;
  
  // Version management
  activeVersionId: string;
  activeVersion: number;
  latestVersion: number;
  
  // Denormalized fields from active version
  status: ScanStatus;
  subStatus?: string;
  thumbnailUrl?: string;
  reconstructionFolder?: string;
  flythroughUrl?: string;
  
  // Storage paths (denormalized from active version)
  tempFolderPath?: string;
  s3FolderPath?: string;
  sequenceUuid?: string;
  gaussModelPath?: string;
  pipelineJsonPath?: string;
  
  // Quick stats
  inputImageCount?: number;
  fileSize?: number;
  quality?: string;
  isFromMobileApp?: boolean;
  
  // Processing timestamps
  processingStartedAt?: Date;
  processingCompletedAt?: Date;
  
  // Standard timestamps
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Create scan DTO matching backend CreateScanDto
 */
export interface CreateScanDto {
  scanId?: string;
  title?: string;
  description?: string;
  visibility?: ScanVisibility;
  tags?: string[];
  category?: string;
  location?: ScanLocation;
  status?: ScanStatus;
  subStatus?: string;
  thumbnailUrl?: string;
  reconstructionFolder?: string;
  flythroughUrl?: string;
  tempFolderPath?: string;
  s3FolderPath?: string;
  sequenceUuid?: string;
  gaussModelPath?: string;
  pipelineJsonPath?: string;
  inputImageCount?: number;
  fileSize?: number;
  quality?: string;
  isFromMobileApp?: boolean;
}

/**
 * Update scan DTO matching backend UpdateScanDto
 */
export interface UpdateScanDto {
  title?: string;
  description?: string;
  visibility?: ScanVisibility;
  tags?: string[];
  category?: string;
  location?: ScanLocation;
  status?: ScanStatus;
  subStatus?: string;
  thumbnailUrl?: string;
  reconstructionFolder?: string;
  flythroughUrl?: string;
  tempFolderPath?: string;
  s3FolderPath?: string;
  sequenceUuid?: string;
  gaussModelPath?: string;
  pipelineJsonPath?: string;
  activeVersionId?: string;
  inputImageCount?: number;
  fileSize?: number;
  quality?: string;
}

/**
 * Create scan version DTO matching backend CreateScanVersionDto
 */
export interface CreateScanVersionDto {
  scanId: string;
  status?: ScanStatus;
  subStatus?: string;
  thumbnailUrl?: string;
  reconstructionFolder?: string;
  flythroughUrl?: string;
  tempFolderPath?: string;
  s3FolderPath?: string;
  sequenceUuid?: string;
  gaussModelPath?: string;
  pipelineJsonPath?: string;
  inputImageCount?: number;
  fileSize?: number;
  quality?: string;
  isFromMobileApp?: boolean;
  changelog?: string;
}

/**
 * Update scan version DTO – mirrors backend UpdateScanVersionDto
 * (same writable fields as CreateScanVersionDto but without scanId)
 */
export interface UpdateScanVersionDto {
  status?: ScanStatus;
  subStatus?: string;
  thumbnailUrl?: string;
  reconstructionFolder?: string;
  flythroughUrl?: string;
  tempFolderPath?: string;
  s3FolderPath?: string;
  sequenceUuid?: string;
  gaussModelPath?: string;
  pipelineJsonPath?: string;
  inputImageCount?: number;
  fileSize?: number;
  quality?: string;
  changelog?: string;
}

/**
 * Set active version DTO
 */
export interface SetActiveVersionDto {
  versionId: string;
}

/**
 * Scan metadata interface matching backend IScanMetadata
 */
export interface ScanMetadata {
  scanId: string;
  metadata: Record<string, any>;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * Create scan metadata DTO matching backend CreateScanMetadataDto
 */
export interface CreateScanMetadataDto {
  scanId: string;
  metadata: Record<string, any>;
}

/**
 * Update scan metadata DTO matching backend UpdateScanMetadataDto
 */
export interface UpdateScanMetadataDto {
  metadata: Record<string, any>;
}

/**
 * Scan filter DTO for querying scans with various criteria
 */
export interface ScanFilterDto {
  creatorId?: string;          // Filter by user who created the scan
  status?: ScanStatus;
  visibility?: ScanVisibility;
  category?: string;
  tags?: string;
  search?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'title';
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/**
 * Reconstruction trigger DTO matching backend TriggerReconstructionDto
 */
export interface TriggerReconstructionDto {
  scanId?: string;
  sequence_set_uuid: string;  // ✅ NEW
  sequences: Array<Array<{    // ✅ NEW (nested arrays)
    image_key: string;
    timestamp: string;
    image_width_px: number;
    image_height_px: number;
    focal_length_35mm?: number;
  }>>;
  options?: Record<string, any>;  // ✅ NEW
  preset?: string;  
}

/**
 * API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Scan creation response
 */
export interface ScanCreationResponse {
  scanId: string;
  scan: Scan;
}

/**
 * Reconstruction status response from /api/reconstructions/:sequenceUuid/status
 */
export interface ReconstructionStatusResponse {
  success: boolean;
  data: {
    sequence_id: number;
    sequence_uuid: string;
    execution_status: 'queued' | 'running' | 'completed' | 'failed' | 'success';
    progress: string;
    gs3d_scene_key?: string;
    env_map_key?: string;
  };
}

/**
 * Enhanced scan status with reconstruction details
 */
export interface EnhancedScanStatus {
  status: ScanStatus;
  subStatus?: string;
  reconstructionStatus?: 'queued' | 'running' | 'completed' | 'failed' | 'success';
  reconstructionProgress?: string;
  lastChecked?: Date;
}

/**
 * Scan lifecycle management interface
 */
export interface ScanLifecycleManager {
  createScanWithStatus(data: CreateScanDto, status: ScanStatus): Promise<ScanCreationResponse>;
  updateScanStatus(scanId: string, status: ScanStatus, subStatus?: string): Promise<Scan>;
  transitionToUploaded(scanId: string, s3FolderPath: string, inputImageCount: number, sequenceUuid: string): Promise<Scan>;
  transitionToProcessing(scanId: string): Promise<Scan>;
  transitionToCompleted(scanId: string, reconstructionFolder?: string): Promise<Scan>;
  transitionToFailed(scanId: string, errorMessage?: string): Promise<Scan>;
  createScanMetadata(scanId: string, metadata: Record<string, any>): Promise<ScanMetadata>;
  updateScanMetadata(scanId: string, metadata: Record<string, any>): Promise<ScanMetadata>;
  createScanVersion(scanId: string, versionData: Omit<CreateScanVersionDto, 'scanId'>): Promise<ScanVersion>;
  getScanVersions(scanId: string): Promise<ScanVersion[]>;
  getScanVersion(versionId: string): Promise<ScanVersion>;
  setActiveScanVersion(scanId: string, versionId: string): Promise<Scan>;
  completeUploadWorkflow: (scanId: string, s3FolderPath: string, fileCount: number, sequenceUuid: string, additionalMetadata?: Record<string, any>) => Promise<Scan>;
}
